import React from 'react';
import { Link2, MailPlus, X } from 'lucide-react';
import { AppModal } from '../../../components/AppModal';
import { AppSelectDropdown } from '../../../components/AppSelectDropdown';
import { UI_Z_INDEX } from '../../../utils/uiZIndex';
import {
  SETTINGS_BTN_OUTLINE,
  SETTINGS_BTN_PRIMARY,
  SETTINGS_FIELD_LABEL,
  SETTINGS_SELECT_TRIGGER_CLASS,
} from '../utils/settingsSectionStyles';
import { MailboxConnectForm } from './email/MailboxConnectForm';
import { setIncomingInvoiceMailboxPending } from './email/incomingInvoiceMailboxPending';
import type { InboxAccount } from './email/types';

function isInboxAccountConnected(acc: InboxAccount): boolean {
  return Boolean(acc.nylas_grant_id) || Boolean(acc.is_demo_simulated_connection);
}

type Step = 'choose' | 'existing' | 'new';

export function IncomingInvoiceMailboxConnectModal({
  open,
  accounts,
  returnPath,
  onClose,
  onAssigned,
  onError,
}: {
  open: boolean;
  accounts: InboxAccount[];
  returnPath: string;
  onClose: () => void;
  onAssigned: (account: InboxAccount) => void;
  onError: (message: string | null) => void;
}) {
  const [step, setStep] = React.useState<Step>('choose');
  const [selectedId, setSelectedId] = React.useState('');
  const [saving, setSaving] = React.useState(false);

  const available = React.useMemo(
    () =>
      accounts.filter((acc) => isInboxAccountConnected(acc) && !acc.incoming_invoice_ingest),
    [accounts],
  );

  React.useEffect(() => {
    if (!open) return;
    setStep('choose');
    setSelectedId('');
    setSaving(false);
    onError(null);
  }, [open, onError]);

  React.useEffect(() => {
    if (step !== 'existing') return;
    if (selectedId && available.some((acc) => String(acc.id) === selectedId)) return;
    setSelectedId(available[0] ? String(available[0].id) : '');
  }, [step, available, selectedId]);

  const assignAccount = async (accountId: string) => {
    setSaving(true);
    onError(null);
    try {
      const res = await fetch(`/api/inbox/accounts/${encodeURIComponent(accountId)}/incoming-invoice`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ incoming_invoice_ingest: true }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error((data as { error?: string }).error || 'Speichern fehlgeschlagen.');
      onAssigned(data as InboxAccount);
      onClose();
    } catch (e) {
      onError(e instanceof Error ? e.message : 'Speichern fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  };

  const title =
    step === 'existing'
      ? 'Bestehendes Postfach verwenden'
      : step === 'new'
        ? 'Neues Postfach verbinden'
        : 'Postfach für Eingangsrechnungen';

  return (
    <AppModal
      open={open}
      onClose={onClose}
      closeOnOverlayClick={!saving}
      disableClose={saving}
      overlayClassName="z-50"
      panelClassName={step === 'new' ? 'md:max-w-2xl md:h-auto' : 'md:max-w-lg md:h-auto'}
      ariaLabelledBy="incoming-invoice-mailbox-title"
    >
      <div className="flex flex-col flex-1 min-h-0">
        <div className="px-5 py-3 border-b border-app-border flex items-start justify-between gap-3 shrink-0">
          <div className="min-w-0">
            <h2 id="incoming-invoice-mailbox-title" className="text-sm font-semibold text-app-text">
              {title}
            </h2>
            <p className="text-xs text-app-text-subtle mt-0.5">
              {step === 'choose'
                ? 'PDF- und XML-Anhänge aus diesem Postfach werden als Eingangsrechnungen übernommen.'
                : step === 'existing'
                  ? 'Wähle ein bereits verbundenes Konto.'
                  : 'Gmail oder IMAP wie unter E-Mail verbinden.'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-app-border text-app-text-subtle hover:bg-app-hover hover:text-app-text disabled:opacity-40"
            aria-label="Schließen"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 overflow-y-auto">
          {step === 'choose' ? (
            <div className="grid gap-3">
              <button
                type="button"
                disabled={available.length === 0}
                onClick={() => setStep('existing')}
                className="rounded-lg border border-app-border bg-app-surface p-4 text-left hover:border-pipedrive-blue hover:bg-blue-50/40 transition-colors disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-app-border disabled:hover:bg-app-surface"
              >
                <div className="flex items-start gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-pipedrive-blue ring-1 ring-blue-100">
                    <Link2 className="h-5 w-5" strokeWidth={1.75} />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-app-text">Aus bestehendem Postfach</p>
                    <p className="mt-1 text-xs text-app-text-muted">
                      {available.length === 0
                        ? 'Kein weiteres verbundenes Postfach verfügbar.'
                        : 'Ein bereits verbundenes Konto für Eingangsrechnungen festlegen.'}
                    </p>
                  </div>
                </div>
              </button>
              <button
                type="button"
                onClick={() => setStep('new')}
                className="rounded-lg border border-app-border bg-app-surface p-4 text-left hover:border-pipedrive-blue hover:bg-blue-50/40 transition-colors"
              >
                <div className="flex items-start gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-pipedrive-blue ring-1 ring-blue-100">
                    <MailPlus className="h-5 w-5" strokeWidth={1.75} />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-app-text">Neues Postfach verbinden</p>
                    <p className="mt-1 text-xs text-app-text-muted">Gmail oder IMAP neu einrichten und fürs Einlesen nutzen.</p>
                  </div>
                </div>
              </button>
            </div>
          ) : null}

          {step === 'existing' ? (
            <div className="space-y-4">
              <div>
                <p className={`mb-1.5 ${SETTINGS_FIELD_LABEL}`}>Postfach</p>
                <AppSelectDropdown
                  value={selectedId}
                  onChange={setSelectedId}
                  options={available.map((acc) => ({ value: String(acc.id), label: acc.email }))}
                  placeholder="Postfach wählen"
                  searchable
                  searchPlaceholder="Postfach suchen…"
                  className="w-full"
                  triggerClassName={SETTINGS_SELECT_TRIGGER_CLASS}
                  menuZIndex={UI_Z_INDEX.portalDropdownInModal}
                />
              </div>
              <div className="flex justify-end gap-2 border-t border-app-border pt-4">
                <button type="button" onClick={() => setStep('choose')} disabled={saving} className={SETTINGS_BTN_OUTLINE}>
                  Zurück
                </button>
                <button
                  type="button"
                  disabled={saving || !selectedId}
                  className={SETTINGS_BTN_PRIMARY}
                  onClick={() => void assignAccount(selectedId)}
                >
                  {saving ? 'Speichert…' : 'Übernehmen'}
                </button>
              </div>
            </div>
          ) : null}

          {step === 'new' ? (
            <MailboxConnectForm
              variant="embedded"
              onCancel={() => setStep('choose')}
              onBeforeGoogleRedirect={() => {
                setIncomingInvoiceMailboxPending({ returnPath });
              }}
              onImapConnected={(account) => {
                void assignAccount(String(account.id));
              }}
              onError={onError}
            />
          ) : null}
        </div>
      </div>
    </AppModal>
  );
}
